/* eslint-disable max-lines */
import { action, computed, observable, toJS } from 'mobx';

import { translate } from 'services/i18nService';
import NotificationService from 'services/NotificationService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import { NOTIFICATION_TYPE } from 'enum/messages';
import {
  SECOND_AS_MILLISECONDS,
  MINUTE_AS_MILLISECONDS,
} from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import { NEW_SHOWS_STATE } from 'enum/videoPlayer';
import { isStringHTMLTag } from 'utils/helpers';

import {
  getEmptyField,
  isNotification,
  sortByTipThreshold,
} from './utils';
import { BOT_TYPES } from './consts';
import chatBotsService from '.';


const TOTAL_FIELDS = {
  [BOT_TYPES.AUTO_NOTIFICATION_BOT]: 6,
  [BOT_TYPES.TIP_RESPONSE_BOT]: 6,
  [BOT_TYPES.WELCOME_BOT]: 1,
};
const AUTO_NOTIFICATION_INTERVAL = 5 * MINUTE_AS_MILLISECONDS;
const TIP_NOTIFICATION_INTERVAL = 5 * MINUTE_AS_MILLISECONDS;

class ChatBotsStore {
  @observable bots = {
    [BOT_TYPES.AUTO_NOTIFICATION_BOT]: {
      botType: 'TIME_BASED',
      enabled: false,
      id: undefined,
      userId: undefined,
      username: undefined,
      details: [
        { message: '' },
        { message: '' },
        { message: '' },
        { message: '' },
        { message: '' },
        { message: '' },
      ],
    },
    [BOT_TYPES.TIP_RESPONSE_BOT]: {
      botType: 'TIP',
      enabled: false,
      id: undefined,
      userId: undefined,
      username: undefined,
      details: [
        { threshold: '', message: '' },
        { threshold: '', message: '' },
        { threshold: '', message: '' },
        { threshold: '', message: '' },
        { threshold: '', message: '' },
        { threshold: '', message: '' },
      ],
    },
    [BOT_TYPES.WELCOME_BOT]: {
      botType: 'WELLCOME',
      enabled: false,
      id: undefined,
      userId: undefined,
      username: undefined,
      details: [
        { message: '' },
      ],
    },
  };

  @observable updateLoadingState = APIStates.INITIAL;
  @observable saveLoadingState = APIStates.INITIAL;
  @observable fetchLoadingState = APIStates.INITIAL;

  autoNotificationMessageTimer = null;
  messageIndex = 0;
  tippingUsers = {};

  @computed get autoNotificationBotDetails() {
    return this.bots[BOT_TYPES.AUTO_NOTIFICATION_BOT].details;
  }

  @computed get tipBotDetails() {
    return this.bots[BOT_TYPES.TIP_RESPONSE_BOT].details;
  }

  @computed get welcomeBotMessage() {
    return this.bots[BOT_TYPES.WELCOME_BOT].details[0].message;
  }

  @action getBotsInfo = async(chatSettingToken) => {
    this.fetchLoadingState = APIStates.PENDING;

    const { data, error } = await chatBotsService.getBotsInfo({ token: chatSettingToken });

    if (error || !data) {
      this.fetchLoadingState = APIStates.ERROR;

      return;
    }

    data.forEach((botData) => {
      this.updateBot(botData.botType, botData);
    });

    this.fetchLoadingState = APIStates.INITIAL;
  };

  @action updateBot = (type, data) => {
    if (!data.details) {
      data.details = [];
    }

    const count = data.details?.length;
    for (let i = count; i < TOTAL_FIELDS[type]; i += 1) {
      data.details.push(getEmptyField(type));
    }

    this.bots[type] = { ...data };

    if (type === BOT_TYPES.AUTO_NOTIFICATION_BOT) {
      this.updateAutoNotifications();
    }
  };

  @action updateAutoNotifications = () => {
    const botEnabled = Boolean(this.bots[BOT_TYPES.AUTO_NOTIFICATION_BOT]?.enabled);
    const alreadyStarted = Boolean(this.autoNotificationMessageTimer);

    const shouldStart = (botEnabled && !alreadyStarted);
    if (shouldStart) {
      let interval = AUTO_NOTIFICATION_INTERVAL;

      const qaAutomatedTestsFlag = localStorage.getItem('CAM4QA_CHAT_BOTS_AUTO_NOTIFICATION_INTERVAL');
      if (qaAutomatedTestsFlag) {
        interval = parseInt(qaAutomatedTestsFlag, 10);
      }

      this.autoNotificationMessageTimer = setInterval(
        this.sendAutoNotificationMessage,
        interval
      );

      return;
    }

    const shouldStop = (!botEnabled && alreadyStarted);
    if (shouldStop) {
      clearInterval(this.autoNotificationMessageTimer);
      this.autoNotificationMessageTimer = null;
    }
  };

  @action onChangeAutoNotificationBotMessage = (index, value) => {
    if (isStringHTMLTag(value)) {
      return;
    }

    const botType = BOT_TYPES.AUTO_NOTIFICATION_BOT;
    const targetField = this.bots[botType].details?.[index];
    if (!targetField) {
      return;
    }

    targetField.message = value;
  };

  @action onChangeTipBotDetails = (index, type, value) => {
    if (isStringHTMLTag(value)) {
      return;
    }

    const botType = BOT_TYPES.TIP_RESPONSE_BOT;
    const targetField = this.bots[botType].details?.[index];
    if (!targetField) {
      return;
    }

    targetField[type] = value;
  };

  @action onChangeWelcomeBotMessage = (value) => {
    if (isStringHTMLTag(value)) {
      return;
    }

    const botType = BOT_TYPES.WELCOME_BOT;
    const targetField = this.bots[botType].details?.[0];
    if (!targetField) {
      return;
    }

    targetField.message = value;
  };

  @action saveBotDetails = async({ actionType, botType }) => {
    const { chatSettingToken } = FirebaseStore;

    const {
      details,
      enabled,
      id,
    } = this.bots[botType];

    let filteredDetails = undefined;
    if (botType === BOT_TYPES.TIP_RESPONSE_BOT) {
      filteredDetails = toJS(details).filter(
        (fieldObj) => Boolean(fieldObj.message) && Boolean(fieldObj.threshold)
      );
    } else {
      filteredDetails = toJS(details).filter(
        (fieldObj) => Boolean(fieldObj.message)
      );
    }

    let newEnabledState = undefined;
    if (actionType === 'SAVE') {
      this.saveLoadingState = APIStates.PENDING;
      newEnabledState = enabled;
    } else {
      this.updateLoadingState = APIStates.PENDING;
      newEnabledState = !enabled;
    }

    const params = {
      botType,
      id,
      options: filteredDetails,
      enabled: newEnabledState,
      token: chatSettingToken,
    };

    const {
      data: responseData,
      error: responseError,
    } = await chatBotsService.saveBot(params);

    if (!responseError) {
      this.updateBot(botType, responseData);

      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.SUCCESS,
        text: translate('v2broadcast.changes.saved'),
      });
    } else {
      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.ERROR,
        text: translate('account.errorGeneric'),
      });
    }

    if (actionType === 'SAVE') {
      this.saveLoadingState = APIStates.INITIAL;
    } else {
      this.updateLoadingState = APIStates.INITIAL;
    }
  };

  @computed get botActionPending() {
    return this.updateLoadingState === APIStates.PENDING;
  }

  @computed get botSavePending() {
    return this.saveLoadingState === APIStates.PENDING;
  }

  @computed get botDataFetchingPending() {
    return this.fetchLoadingState === APIStates.PENDING;
  }

  @computed get botDataFetchingError() {
    return this.fetchLoadingState === APIStates.ERROR;
  }

  @computed get isAnyBotActive() {
    return (
      this.bots[BOT_TYPES.AUTO_NOTIFICATION_BOT]?.enabled ||
      this.bots[BOT_TYPES.TIP_RESPONSE_BOT]?.enabled ||
      this.bots[BOT_TYPES.WELCOME_BOT]?.enabled
    );
  }

  @computed get allActiveBots() {
    const result = [];
    if (this.bots[BOT_TYPES.AUTO_NOTIFICATION_BOT]?.enabled) {
      result.push(BOT_TYPES.AUTO_NOTIFICATION_BOT);
    }
    if (this.bots[BOT_TYPES.TIP_RESPONSE_BOT]?.enabled) {
      result.push(BOT_TYPES.TIP_RESPONSE_BOT);
    }
    if (this.bots[BOT_TYPES.WELCOME_BOT]?.enabled) {
      result.push(BOT_TYPES.WELCOME_BOT);
    }

    return result;
  }

  _isUserAnonymous = (username) => {
    const { sortedUsers } = FirebaseStore;

    const currentUsername = username?.toLowerCase();
    const currentUser = sortedUsers.find(
      (item) => item.originalUsername?.toLowerCase() === currentUsername
    );

    return Boolean(currentUser?.stealthLevel);
  };

  _isBroadcasterInPrivateOrGroupShow = () => {
    const { newShowsState } = FirebaseStore;
    const { INSIDE_GS, INSIDE_PS } = NEW_SHOWS_STATE;
    const isInPrivateOrGroupShow = [INSIDE_GS, INSIDE_PS].includes(newShowsState);

    return isInPrivateOrGroupShow;
  };


  _canSendAutoNotificationMessage = () => {
    const { messages } = ChatStore;
    const { user: { username } } = UserStore;

    if (
      !UserStore.hasCF(allUserGroups.CHAT_BOTS) ||
      !this.bots[BOT_TYPES.AUTO_NOTIFICATION_BOT]?.enabled ||
      (messages?.length < 3) ||
      this._isBroadcasterInPrivateOrGroupShow()
    ) {
      return false;
    }

    const lastThreeMesagesIndex = -3;
    const lastThreeMesages = messages.slice(lastThreeMesagesIndex);

    const areAnyLastMessagesNotifications = lastThreeMesages.some(
      (message) => {
        const { senderOriginalUsername, type } = message;
        const isNotificationMessage = isNotification(type);
        const isBotOrPerformerMessage = senderOriginalUsername?.toLowerCase() === username?.toLowerCase();

        return (isNotificationMessage || isBotOrPerformerMessage);
      }
    );
    if (areAnyLastMessagesNotifications) {
      return false;
    }

    return true;
  };

  sendAutoNotificationMessage = () => {
    if (!this._canSendAutoNotificationMessage()) {
      return;
    }

    const { user: { username, accessHash } } = UserStore;
    const timeBasedBot = this.bots[BOT_TYPES.AUTO_NOTIFICATION_BOT];

    const messages = toJS(timeBasedBot.details).filter((item) => item.message?.trim());
    const currentIndex = this.messageIndex % messages.length;
    const message = messages[currentIndex]?.message;

    ChatStore.sendMainChatMessage(
      username,
      accessHash,
      username,
      message
    );

    this.messageIndex += 1;
  };

  sendTippingBotMessage = (message, roomName) => {
    if (
      !UserStore.hasCF(allUserGroups.CHAT_BOTS) ||
      this._isUserAnonymous(message.senderOriginalUsername) ||
      !message?.senderOriginalUsername
    ) {
      return;
    }

    const { enabled, details } = this.bots[BOT_TYPES.TIP_RESPONSE_BOT];
    if (!enabled || !details) {
      return;
    }

    const { senderOriginalUsername } = message || {};
    const lastMessageTimestamp = this.tippingUsers[senderOriginalUsername];
    if (lastMessageTimestamp) {
      let cooldown = TIP_NOTIFICATION_INTERVAL;

      const qaAutomatedTestsFlag = localStorage.getItem('CAM4QA_CHAT_BOTS_TIP_RESPONSE_INTERVAL');
      if (qaAutomatedTestsFlag) {
        cooldown = parseInt(qaAutomatedTestsFlag, 10);
      }

      const timePassed = Date.now() - lastMessageTimestamp;
      if (timePassed <= cooldown) {
        return;
      }
    }

    const parsedNotificationExtra = JSON.parse(message.notificationExtra);
    const tipValue = message.gameName ? parsedNotificationExtra?.p : message.notificationExtra;
    const tipAmount = parseInt(tipValue, 10);

    const sortedOptions = details.sort(sortByTipThreshold);
    const currentThreshold = sortedOptions.find(
      (option) => tipAmount >= option.threshold
    );

    if (!currentThreshold) {
      return;
    }

    const { user: { accessHash, username } } = UserStore;
    const botMessage = currentThreshold.message.replaceAll('{user}', senderOriginalUsername);

    setTimeout(() => {
      ChatStore.sendMainChatMessage(
        username,
        accessHash,
        roomName,
        botMessage
      );
    }, 4 * SECOND_AS_MILLISECONDS);

    this.tippingUsers[senderOriginalUsername] = Date.now();
  };

  sendWelcomeBotMessage = async() => {
    const { chatSettingToken } = FirebaseStore;
    const {
      profileInfo: {
        userId,
        username: performerName,
      },
    } = ProfileStore;
    const { user: { username } } = UserStore;

    if (
      !UserStore.hasCF(allUserGroups.CHAT_BOTS) ||
      this._isBroadcasterInPrivateOrGroupShow() ||
      this._isUserAnonymous(username)
    ) {
      return;
    }

    const isMyProfile = performerName?.toLowerCase() === username?.toLowerCase();
    if (isMyProfile) {
      return;
    }

    const lsFlag = `hasBeenIn${performerName}RoomInLastTwoHours`;
    const hasBeenInRoomInLastTwoHours = Boolean(localStorage.getItem(lsFlag));
    if (hasBeenInRoomInLastTwoHours) {
      return;
    }

    const { data, error } = await chatBotsService.getWelcomeBotInfoForViewer({ token: chatSettingToken, userId });
    if (!data || error) {
      return;
    }

    const isWelcomeBotEnabled = data?.enabled;
    if (!isWelcomeBotEnabled) {
      return;
    }

    const messageValue = data.details[0].message;
    const message = messageValue.replaceAll('{user}', username);

    setTimeout(() => {
      ChatStore.addMessage({
        type: NOTIFICATION_TYPE.MESSAGE,
        message,
        senderOriginalUsername: performerName,
        messageKey: performerName,
      });
    }, 5 * SECOND_AS_MILLISECONDS);

    this.setUserHasEnteredRoomCookie(performerName);
  };

  setUserHasEnteredRoomCookie = (profileName) => {
    const cookieExpires = new Date();
    cookieExpires.setHours(new Date().getHours() + 2);
    localStorage.setItem(`hasBeenIn${profileName}RoomInLastTwoHours`, true, {
      path: '/',
      expires: cookieExpires,
    });
  };
}

export default new ChatBotsStore();
